import React from "react";
import { v5 as uuidv5 } from "uuid";
import { Skeleton, Stack } from "@mui/material";

import { Partner, PartnerResource } from "../../../../api/partners";
import { VideoPlatformResource } from "../../../../api/video_platform";
import { DetailField } from "./DetailField";

const GCAL_MEETING_NAMESPACE = "6ba7b814-9dad-11d1-80b4-00c04fd430c8";

interface Props {
  gcalEventId: string;
  recurringMeetingId?: string;
  refreshKey?: number;
}

/**
 * Read-only summary of a meeting's partner association for the details view:
 * "Private — {partner}" (partner-scoped), "Preferred — {partner}" (differentiated
 * card), or "None" — so an admin can see it without opening the edit dialog.
 */
export const PartnerAssociationReadOnly: React.FC<Props> = ({
  gcalEventId,
  recurringMeetingId,
  refreshKey,
}) => {
  const hostedMeetingId = React.useMemo(
    () => uuidv5(gcalEventId, GCAL_MEETING_NAMESPACE),
    [gcalEventId],
  );
  const stableKey = recurringMeetingId ?? gcalEventId;

  const [loading, setLoading] = React.useState(true);
  const [value, setValue] = React.useState("None");

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);

    Promise.all([
      // Light in-process read: only partnerId, no 2-hop full-meeting fetch.
      // found:false (no video-platform record yet) is the same as unscoped.
      VideoPlatformResource.getMeetingPartnerAssociationCached(
        hostedMeetingId,
      ).then((a) => a.partnerId),
      PartnerResource.getListCached().catch(() => [] as Partner[]),
    ])
      .then(([partnerId, partners]) => {
        if (cancelled) return;
        if (partnerId) {
          const name =
            partners.find((p) => p.id === partnerId)?.displayName ??
            "Unknown partner";
          setValue(`Private — ${name}`);
          return;
        }
        // Preferred is one-to-many: list EVERY partner that marked it.
        const owners = partners.filter((p) =>
          p.preferredSessionIds?.includes(stableKey),
        );
        setValue(
          owners.length
            ? `Preferred — ${owners.map((o) => o.displayName).join(", ")}`
            : "None",
        );
      })
      .catch(() => {
        if (!cancelled) setValue("—");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [hostedMeetingId, stableKey, refreshKey]);

  if (loading) {
    return <Skeleton variant="text" width={200} height={24} />;
  }

  return (
    <Stack direction="row" gap={2} alignItems="center">
      <DetailField field="Partner Access" value={value} />
    </Stack>
  );
};
